import React, { useMemo } from "react";
import {
  Alert,
  Card,
  Col,
  Empty,
  Progress,
  Row,
  Skeleton,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
} from "antd";
import {
  DatabaseOutlined,
  FileOutlined,
  InboxOutlined,
  PartitionOutlined,
} from "@ant-design/icons";
import { formatDistanceToNow } from "date-fns";
import { it } from "date-fns/locale";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title as ChartTitle,
  Tooltip as ChartTooltip,
  Legend,
} from "chart.js";
import { Chart } from "react-chartjs-2";
import { formatBytes } from "../../../utils";
import useOrgStorage from "../hooks/useOrgStorage";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ChartTitle,
  ChartTooltip,
  Legend
);

const { Text, Title } = Typography;

const formatClassLabel = (className) => {
  if (!className || className === "__unknown__") {
    return "Non classificato";
  }

  return className;
};

const formatRelativeDate = (value) => {
  if (!value) return "Non disponibile";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Non disponibile";

  return `${date.toLocaleString("it-IT")} (${formatDistanceToNow(date, {
    addSuffix: true,
    locale: it,
  })})`;
};

const buildBarChartOptions = ({ isBytes = false }) => ({
  indexAxis: "y",
  responsive: true,
  maintainAspectRatio: false,
  animation: {
    duration: 250,
  },
  plugins: {
    legend: {
      display: false,
    },
    tooltip: {
      callbacks: {
        label: (context) => {
          const value = Number(context.raw || 0);
          return isBytes ? formatBytes(value) : `${value.toLocaleString("it-IT")} file`;
        },
      },
    },
  },
  scales: {
    x: {
      beginAtZero: true,
      ticks: {
        callback: (value) =>
          isBytes
            ? formatBytes(Number(value || 0), Number(value) >= 1024 ? 1 : 0)
            : Number(value || 0).toLocaleString("it-IT"),
      },
    },
    y: {
      ticks: {
        autoSkip: false,
      },
    },
  },
});

const metricCardStyle = {
  height: "100%",
  borderRadius: 16,
  boxShadow: "0 10px 28px rgba(15, 23, 42, 0.08)",
};

const OrgStorage = ({ orgId }) => {
  const { storage, loading, error } = useOrgStorage(orgId);

  const breakdownEntries = useMemo(() => {
    const breakdown =
      storage?.breakdown && typeof storage.breakdown === "object"
        ? storage.breakdown
        : {};

    return Object.entries(breakdown)
      .map(([className, stats]) => {
        const bytes = Number(stats?.bytes || 0);
        const files = Number(stats?.files || 0);

        return {
          key: className,
          className,
          label: formatClassLabel(className),
          bytes,
          files,
          avgBytes: files > 0 ? bytes / files : 0,
        };
      })
      .sort((left, right) => {
        if (right.bytes !== left.bytes) return right.bytes - left.bytes;
        if (right.files !== left.files) return right.files - left.files;
        return left.label.localeCompare(right.label, "it");
      });
  }, [storage]);

  const totalBytes = Number(storage?.total_bytes || 0);
  const totalFiles = Number(storage?.total_files || 0);
  const totalClasses = breakdownEntries.length;
  const averageFileSize = totalFiles > 0 ? totalBytes / totalFiles : 0;
  const topClass = breakdownEntries[0] || null;
  const topClassPercentage =
    totalBytes > 0 && topClass ? (topClass.bytes / totalBytes) * 100 : 0;
  const chartHeight = Math.max(320, breakdownEntries.length * 42);

  const bytesChartData = useMemo(
    () => ({
      labels: breakdownEntries.map((entry) => entry.label),
      datasets: [
        {
          label: "Spazio occupato",
          data: breakdownEntries.map((entry) => entry.bytes),
          backgroundColor: "rgba(22, 119, 255, 0.78)",
          borderColor: "rgba(22, 119, 255, 1)",
          borderWidth: 1,
          borderRadius: 8,
          maxBarThickness: 28,
        },
      ],
    }),
    [breakdownEntries]
  );

  const filesChartData = useMemo(
    () => ({
      labels: breakdownEntries.map((entry) => entry.label),
      datasets: [
        {
          label: "Numero file",
          data: breakdownEntries.map((entry) => entry.files),
          backgroundColor: "rgba(82, 196, 26, 0.75)",
          borderColor: "rgba(82, 196, 26, 1)",
          borderWidth: 1,
          borderRadius: 8,
          maxBarThickness: 28,
        },
      ],
    }),
    [breakdownEntries]
  );

  const columns = useMemo(
    () => [
      {
        title: "Classe",
        dataIndex: "label",
        key: "label",
        render: (_, record) => (
          <Space>
            <Tag color={record.className === "__unknown__" ? "default" : "blue"}>
              {record.label}
            </Tag>
          </Space>
        ),
      },
      {
        title: "Spazio",
        dataIndex: "bytes",
        key: "bytes",
        sorter: (left, right) => left.bytes - right.bytes,
        defaultSortOrder: "descend",
        render: (value) => formatBytes(value),
      },
      {
        title: "File",
        dataIndex: "files",
        key: "files",
        sorter: (left, right) => left.files - right.files,
        render: (value) => value.toLocaleString("it-IT"),
      },
      {
        title: "Media / file",
        dataIndex: "avgBytes",
        key: "avgBytes",
        sorter: (left, right) => left.avgBytes - right.avgBytes,
        render: (value) => formatBytes(value),
      },
      {
        title: "% sul totale",
        key: "share",
        render: (_, record) => {
          const percentage = totalBytes > 0 ? (record.bytes / totalBytes) * 100 : 0;
          return `${percentage.toFixed(1)}%`;
        },
      },
    ],
    [totalBytes]
  );

  if (loading) {
    return <Skeleton active paragraph={{ rows: 10 }} />;
  }

  if (error) {
    return (
      <Alert
        type="error"
        showIcon
        message="Impossibile caricare il report storage"
        description={error}
      />
    );
  }

  if (!storage) {
    return (
      <Card>
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="Nessun dato di storage disponibile per questa organizzazione."
        >
          <Text type="secondary">
            Il riepilogo dello spazio non è ancora disponibile.
          </Text>
        </Empty>
      </Card>
    );
  }

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Card
        style={{
          borderRadius: 20,
          background:
            "linear-gradient(135deg, rgba(9, 109, 217, 0.08), rgba(82, 196, 26, 0.08))",
          border: "1px solid rgba(9, 109, 217, 0.12)",
        }}
      >
        <Space direction="vertical" size={8} style={{ width: "100%" }}>
          <Title level={4} style={{ margin: 0 }}>
            Utilizzo spazio archivio
          </Title>
          <Text type="secondary">
            Riepilogo dello spazio occupato e distribuzione per classi di contenuto.
          </Text>
          <Text type="secondary">Ultimo aggiornamento: {formatRelativeDate(storage.updatedAt)}</Text>
        </Space>
      </Card>

      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} xl={6}>
          <Card style={metricCardStyle}>
            <Statistic
              title="Spazio occupato"
              value={totalBytes}
              formatter={(value) => formatBytes(Number(value || 0))}
              prefix={<DatabaseOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <Card style={metricCardStyle}>
            <Statistic
              title="File censiti"
              value={totalFiles}
              formatter={(value) => Number(value || 0).toLocaleString("it-IT")}
              prefix={<FileOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <Card style={metricCardStyle}>
            <Statistic
              title="Classi aggregate"
              value={totalClasses}
              formatter={(value) => Number(value || 0).toLocaleString("it-IT")}
              prefix={<PartitionOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <Card style={metricCardStyle}>
            <Statistic
              title="Dimensione media file"
              value={averageFileSize}
              formatter={(value) => formatBytes(Number(value || 0))}
              prefix={<InboxOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {topClass ? (
        <Card title="Classe prevalente" style={{ borderRadius: 18 }}>
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} md={10}>
              <Space direction="vertical" size={4}>
                <Tag color="blue" style={{ width: "fit-content", marginInlineEnd: 0 }}>
                  {topClass.label}
                </Tag>
                <Text strong>{formatBytes(topClass.bytes)}</Text>
                <Text type="secondary">
                  {topClass.files.toLocaleString("it-IT")} file, media{" "}
                  {formatBytes(topClass.avgBytes)}
                </Text>
              </Space>
            </Col>
            <Col xs={24} md={14}>
              <Progress
                percent={Number(topClassPercentage.toFixed(1))}
                strokeColor="#1677ff"
                format={(percent) => `${percent}% del totale`}
              />
            </Col>
          </Row>
        </Card>
      ) : (
        <Alert
          type="warning"
          showIcon
          message="Il dettaglio per classe non è ancora disponibile."
        />
      )}

      {breakdownEntries.length > 0 && (
        <>
          <Row gutter={[16, 16]}>
            <Col xs={24} xl={12}>
              <Card title="Spazio occupato per classe" style={{ borderRadius: 18 }}>
                <div style={{ height: chartHeight }}>
                  <Chart
                    type="bar"
                    data={bytesChartData}
                    options={buildBarChartOptions({ isBytes: true })}
                  />
                </div>
              </Card>
            </Col>
            <Col xs={24} xl={12}>
              <Card title="Numero file per classe" style={{ borderRadius: 18 }}>
                <div style={{ height: chartHeight }}>
                  <Chart
                    type="bar"
                    data={filesChartData}
                    options={buildBarChartOptions({ isBytes: false })}
                  />
                </div>
              </Card>
            </Col>
          </Row>

          <Card title="Dettaglio classi aggregate" style={{ borderRadius: 18 }}>
            <Table
              rowKey="key"
              dataSource={breakdownEntries}
              columns={columns}
              size="small"
              pagination={false}
            />
          </Card>
        </>
      )}
    </Space>
  );
};

export default OrgStorage;
